<template>
  <!--
    Feuille du certificat.

    Toute la typographie est exprimée en `cqw` (largeur du conteneur) : l'aperçu
    est donc une réduction FIDÈLE d'une page A4 portrait, lisible quelle que
    soit la largeur disponible. C'est ce qui règle le problème de format —
    l'aperçu ne « déborde » plus et ne se déforme plus selon la colonne.
  -->
  <div class="cert-shell">
    <div class="cert-sheet" :class="`cert-${data.template}`" :style="paletteStyle">
      <div class="cert-band" v-if="data.template === 'bandeau'" aria-hidden="true"></div>

      <div class="cert-frame">
        <!--
          En-tête bilingue.

          Chaque ligne occupe une rangée de la grille : la version française et
          la version anglaise sont donc dans la MÊME rangée, et partagent
          automatiquement sa hauteur. Si l'une des deux passe sur deux lignes,
          l'autre reste alignée avec elle — c'est ce qui garantit l'équilibre
          visuel quelle que soit la longueur des textes.

          Le logo occupe la colonne centrale sur toute la hauteur.
        -->
        <header class="cert-head">
          <div class="cert-logo-cell" :style="logoSpan">
            <img v-if="logo" class="cert-logo" :src="logo" :alt="$t('certificate.logo_alt')" />
            <div v-else class="cert-logo cert-logo-empty" aria-hidden="true"></div>
          </div>

          <template v-for="(row, i) in headerRows" :key="row.id || `row-${i}`">
            <div class="cert-head-cell cert-head-fr" :class="{ 'is-italic': row.italic }">
              <span
                class="cert-head-text"
                :ref="(el) => setCell(i, 'fr', el)"
                :style="widthOf(i, 'fr')"
                >{{ row.fr }}</span
              >
            </div>
            <div class="cert-head-cell cert-head-en" :class="{ 'is-italic': row.italic }">
              <span
                class="cert-head-text"
                :ref="(el) => setCell(i, 'en', el)"
                :style="widthOf(i, 'en')"
                >{{ row.en }}</span
              >
            </div>
          </template>
        </header>

        <h1 class="cert-title" style="color: black !important;">{{ data.title }}</h1>

        <div class="cert-body">
          <p class="cert-paragraph" v-for="(paragraph, i) in paragraphs" :key="`p-${i}`">
            <template v-for="(segment, j) in segmentsOf(paragraph)" :key="`seg-${i}-${j}`">
              <strong class="cert-strong" v-if="segment.bold">{{ segment.text }}</strong>
              <span v-else>{{ segment.text }}</span>
            </template>
          </p>
        </div>

        <footer class="cert-footer">
          <p class="cert-place">
            {{ placePrefix }}<strong class="cert-strong">{{ displayDate }}</strong>
          </p>
          <div class="cert-sign">
            <p class="cert-sign-role">{{ data.signatory_role }}</p>
            <div class="cert-seal" v-if="data.show_seal" aria-hidden="true"></div>
            <div class="cert-sign-space" v-else aria-hidden="true"></div>
            <p class="cert-sign-name">{{ data.signatory_name }}</p>
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { paletteOf, splitBold, splitParagraphs } from "@/utils/certificateModel";
import { schoolProfile } from "@/utils/schoolProfile";

export default {
  name: "certificate-sheet",
  props: {
    data: { type: Object, required: true },
  },
  data() {
    return {
      /**
       * Contrainte de largeur calculée pour chaque cellule, EN POURCENTAGE.
       * Forme : { "0-en": 68.4, "2-fr": 100, ... }
       */
      cellWidths: {},
    };
  },
  mounted() {
    this.scheduleSync();
    // Les polices sont chargées en différé : une mesure faite avant leur
    // arrivée porterait sur la police de repli et serait fausse.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.scheduleSync());
    }
  },
  beforeUnmount() {
    if (this.syncTimer) clearTimeout(this.syncTimer);
  },
  watch: {
    // Toute modification de l'en-tête relance la mesure.
    "data.header_rows": { handler() { this.scheduleSync(); }, deep: true },
  },
  computed: {
    palette() {
      return paletteOf(this.data.palette);
    },
    paletteStyle() {
      return {
        "--paper": this.palette.paper,
        "--ink": this.palette.ink,
        "--soft": this.palette.soft,
        "--accent": this.palette.accent,
      };
    },
    /**
     * Le logo doit couvrir toutes les rangées de l'en-tête pour rester centré
     */
    logoSpan() {
      return { gridRow: `1 / span ${Math.max(this.headerRows.length, 1)}` };
    },
    /** Lignes de l'en-tête, en ignorant celles restées entièrement vides. */
    headerRows() {
      return (this.data.header_rows || []).filter(
        (row) => String(row.fr || "").trim() || String(row.en || "").trim()
      );
    },
    /**
     * Logo de l'établissement.
     *
     * Il provient de la configuration système (fiche établissement) et n'est
     * pas un réglage du certificat : il n'est ni saisi ici, ni enregistré
     * côté API. `schoolProfile` étant réactif, un changement de logo dans la
     * configuration se répercute partout sans intervention.
     *
     * `data.logo_url` reste consulté en premier pour les configurations
     * enregistrées avant ce changement, qui portaient encore une URL.
     */
    logo() {
      return String(this.data.logo_url || schoolProfile.logo || "").trim();
    },
    paragraphs() {
      return splitParagraphs(this.data.body);
    },
    placePrefix() {
      const place = (this.data.place || "").trim();
      return place ? `${place}, ${this.$t("certificate.the")} ` : `${this.$t("certificate.the")} `;
    },
    /** Date saisie, ou date du jour formatée dans la langue courante. */
    displayDate() {
      const typed = (this.data.date || "").trim();
      const locale = this.$i18n.locale === "fr" ? "fr-FR" : "en-US";
      if (typed) {
        const parsed = new Date(typed);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleDateString(locale, {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
        return typed;
      }
      return new Date().toLocaleDateString(locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
  },
  methods: {
    /** Mémorise l'élément de texte d'une cellule pour pouvoir le mesurer. */
    setCell(index, side, el) {
      if (!this.cellRefs) this.cellRefs = {};
      if (el) this.cellRefs[`${index}-${side}`] = el;
      else delete this.cellRefs[`${index}-${side}`];
    },
    widthOf(index, side) {
      const value = this.cellWidths[`${index}-${side}`];
      return value ? { maxWidth: `${value}%` } : null;
    },
    /**
     * Nombre de lignes réellement occupées par un texte.
     */
    countLines(el) {
      if (!el || !el.firstChild) return 1;
      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = Array.from(range.getClientRects());
      if (!rects.length) return 1;
      const tops = new Set(rects.map((rect) => Math.round(rect.top * 4) / 4));
      return Math.max(tops.size, 1);
    },
    /**
     * Resserre une cellule jusqu'à ce qu'elle occupe le nombre de lignes visé.
     */
    fitToLines(el, target) {
      let low = 30;
      let high = 100;
      let best = 100;
      for (let step = 0; step < 8; step += 1) {
        const mid = (low + high) / 2;
        el.style.maxWidth = `${mid}%`;
        if (this.countLines(el) >= target) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }
      el.style.maxWidth = `${best}%`;
      return best;
    },
    /**
     * Aligne les deux colonnes ligne à ligne.
     */
    syncHeaderLines() {
      const refs = this.cellRefs || {};
      const widths = {};

      this.headerRows.forEach((row, index) => {
        const fr = refs[`${index}-fr`];
        const en = refs[`${index}-en`];
        if (!fr || !en) return;

        // On repart sans contrainte pour mesurer le repli naturel.
        fr.style.maxWidth = "";
        en.style.maxWidth = "";
        const frLines = this.countLines(fr);
        const enLines = this.countLines(en);
        const target = Math.max(frLines, enLines);

        widths[`${index}-fr`] = frLines < target ? this.fitToLines(fr, target) : 100;
        widths[`${index}-en`] = enLines < target ? this.fitToLines(en, target) : 100;
      });

      this.cellWidths = widths;
    },
    /** Regroupe les recalculs successifs en un seul. */
    scheduleSync() {
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.$nextTick(() => this.syncHeaderLines());
      }, 40);
    },
    segmentsOf(paragraph) {
      return splitBold(paragraph);
    },
  },
};
</script>

<style scoped>

.cert-shell {
  container-type: inline-size;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

.cert-sheet {
  aspect-ratio: 275 / 297;
  width: 100%;
  background-color: var(--paper);
  color: var(--ink);
  font-family: "Work Sans", "Open Sans", "Helvetica Neue", Arial, sans-serif;
  box-shadow: 0 1px 2px rgba(20, 22, 26, 0.08), 0 18px 44px -18px rgba(20, 22, 26, 0.35);
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.cert-frame {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 5cqw 7.5cqw 6cqw;
}

/* --- En-tête ------------------------------------------------------------ */

.cert-head {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  column-gap: 2cqw;
  align-items: stretch;
}

/* Le logo occupe la colonne du milieu sur toute la hauteur de l'en-tête. */
.cert-logo-cell {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.8cqw;
}

/*
  Dense, à la manière des en-têtes officiels : les lignes sont serrées les
  unes contre les autres, le bloc entier occupe peu de hauteur.
*/
.cert-head-cell {
  position: relative;
  font-size: 1.75cqw;
  line-height: 1.25;
  letter-spacing: 0.01em;
  color: var(--ink);
  text-align: center;
  padding: 0.15cqw 0 0.55cqw;
  display: flex;
  /*
    Alignement sur le HAUT de la rangée, et non centré.
  */
  align-items: flex-start;
  justify-content: center;
  overflow-wrap: anywhere;
}

.cert-head-text {
  display: inline-block;
  text-align: center;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;
}

.cert-head-cell::after {
  content: "";
  position: absolute;
  bottom: 0.16cqw;
  left: 50%;
  transform: translateX(-50%);
  width: 4cqw;
  height: 0.08cqw;
  background-color: var(--soft);
  opacity: 0.5;
}

/* Pas de trait sous la dernière ligne : le titre suit juste après. */
.cert-head-cell:nth-last-child(-n + 2)::after {
  display: none;
}

.cert-head-fr {
  grid-column: 1;
}

.cert-head-en {
  grid-column: 3;
}

/* Style porté par la ligne elle-même (la devise, par convention). */
.cert-head-cell.is-italic {
  font-style: italic;
}

.cert-logo {
  width: 11.5cqw;
  height: 11.5cqw;
  object-fit: contain;
}

.cert-logo-empty {
  border: 0.18cqw dashed var(--soft);
  opacity: 0.55;
  border-radius: 0.6cqw;
}

/* --- Titre -------------------------------------------------------------- */

.cert-title {
  font-family: "Cormorant Garamond", "Iowan Old Style", Georgia, serif;
  font-size: 6.4cqw;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  letter-spacing: 0.01em;
  margin: 5.5cqw 0 0;
  color: var(--ink);
}

.cert-title::after {
  content: "";
  display: block;
  width: 16cqw;
  height: 0.25cqw;
  background-color: var(--accent);
  margin: 1.8cqw auto 0;
}

/* --- Corps -------------------------------------------------------------- */

.cert-body {
  margin-top: 5cqw;
  display: flex;
  flex-direction: column;
  gap: 2.6cqw;
}

.cert-paragraph {
  margin: 0;
  font-size: 2.1cqw;
  line-height: 1.9;
  text-align: justify;
  color: var(--ink);
}

.cert-strong {
  font-weight: 600;
  color: var(--accent);
}

/* --- Pied de page ------------------------------------------------------- */

.cert-footer {
  margin-top: auto;
  padding-top: 5cqw;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.6cqw;
}

.cert-place {
  margin: 0;
  font-size: 2.1cqw;
  color: var(--ink);
}

.cert-sign {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2cqw;
  min-width: 32cqw;
}

.cert-sign-role {
  margin: 0;
  color: var(--soft);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-size: 1.5cqw;
}

.cert-sign-name {
  margin: 0;
  font-family: "Cormorant Garamond", "Iowan Old Style", Georgia, serif;
  font-size: 2.4cqw;
  padding-top: 1cqw;
  border-top: 0.12cqw solid var(--soft);
  width: 100%;
  text-align: center;
  color: var(--ink);
}

.cert-seal {
  width: 13cqw;
  height: 13cqw;
  border: 0.18cqw dashed var(--accent);
  opacity: 0.5;
  border-radius: 999px;
}

.cert-sign-space {
  height: 6cqw;
}

/* --- Variantes de modèles ---------------------------------------------- */

.cert-officiel .cert-frame {
  border: 0.18cqw solid var(--soft);
  inset: 2.4cqw;
  padding: 4cqw 6cqw 5cqw;
}

.cert-sobre .cert-title {
  text-transform: uppercase;
  font-family: "Work Sans", "Open Sans", Arial, sans-serif;
  font-weight: 300;
  letter-spacing: 0.14em;
  font-size: 4.6cqw;
}

.cert-bandeau .cert-band {
  position: absolute;
  inset: 0 0 auto;
  height: 2.2cqw;
  background-color: var(--accent);
}

.cert-bandeau .cert-frame {
  padding-top: 9cqw;
}

.cert-bandeau .cert-title {
  color: var(--accent);
}
</style>
